import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { appBarTheme, secondaryColor } from 'theme/theme';
import clsx from 'clsx';

import {
  Button,
  AppBar,
  Toolbar,
  Drawer,
  ThemeProvider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Navigation } from 'shared/Navigation';

import 'translations/translations';
import logo from 'static/media/logo_white-3x.png';
import { Link, useParams } from 'react-router-dom';
import { ApplicationState } from 'store';
import { AuthRoutes } from 'shared/Routes';
import { organizationActions } from 'actions/organizationActions';
import { DepartmentSelect } from 'features/departments/DepartmentSelect';
import { getTokenData } from 'helpers/decodeToken';
import { SessionStorageService } from 'services/sessionStorageService';

const drawerWidth = 200;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    height: '100vh',
  },

  toolbarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...theme.mixins.toolbar,
  },
  appBar: {
    padding: 0,
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: 'none',
  },
  title: {
    flexGrow: 1,
    display: 'flex',
  },
  drawerPaper: {
    padding: '4rem 0.3rem 0 0.3rem',
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    backgroundColor: secondaryColor(),
    color: '#fff',
  },
  drawerPaperClose: {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: 0,
  },
  appBarSpacer: { height: 64, minHeight: 64, width: '100%' },
  content: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    height: '100%',
    width: '100%',
    overflow: 'auto',
    padding: 0,
    margin: 0,
  },
  container: {
    padding: 0,
    margin: 0,
  },
  pageContainer: {
    padding: 0,
    margin: 0,
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    flex: 1,
  },
  img: {
    maxWidth: '9rem',
  },
  organizationsButton: {
    marginTop: '0',
    marginBottom: '0',
    marginLeft: '2.5rem',
  },
  orgName: {
    color: '#fff',
    fontSize: '1rem',
    marginRight: '1rem',
  },
  deptSelect: {
    marginRight: '1rem',
  },
}));

interface LayoutProps {
  children?: React.ReactNode;
}

interface ParamTypes {
  orgId: string;
}

const AppLayout: React.FC<LayoutProps> = (props) => {
  const { children } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation(['translations']);
  const { orgId } = useParams<ParamTypes>();

  const userAccess = useSelector(
    (state: ApplicationState) => state.isLoggedIn?.userAccess,
  );

  const organizationName: string | undefined = useSelector(
    (state: ApplicationState) => state.organization?.organization?.name,
  );

  const showList =
    (userAccess?.isGlobalAdmin ||
      (userAccess?.organizations && userAccess?.organizations?.length > 1)) &&
    location?.pathname?.indexOf('organizations') === -1;

  const linkToOrg =
    !!orgId &&
    (userAccess?.isGlobalAdmin ||
      userAccess?.organizations?.find((org) => org.id === orgId));

  useEffect(() => {
    dispatch(organizationActions.clearOrganization());
    if (orgId) {
      dispatch(organizationActions.getOrganizationById(orgId));
    }
  }, [orgId]);

  useEffect(() => {
    const token = SessionStorageService.getToken();
    if (token) {
      const tokenData = getTokenData(token);

      if (tokenData.isGlobalAdmin) {
        dispatch({
          type: 'SET_USER_INFO',
          userInfo: {
            isGlobalAdmin: true,
          },
        });
      }
    }
  }, []);

  return (
    <div className={classes.root}>
      <ThemeProvider theme={appBarTheme}>
        <AppBar position="absolute" className={clsx(classes.appBar)}>
          <Toolbar>
            <div className={classes.title}>
              <div>
                <img src={logo} alt="Logo" className={classes.img} />
              </div>
              {showList && (
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    component={Link}
                    to={AuthRoutes.Organizations}
                    className={classes.organizationsButton}
                  >
                    {t('navigation.organizations')}
                  </Button>
                </div>
              )}
            </div>

            {orgId ? (
              <>
                {linkToOrg ? (
                  <Link
                    to={`/organization/${orgId}`}
                    className={classes.orgName}
                  >
                    {organizationName}
                  </Link>
                ) : (
                  <div className={classes.orgName}>{organizationName}</div>
                )}
                <div className={classes.deptSelect}>
                  <DepartmentSelect />
                </div>
              </>
            ) : null}
          </Toolbar>
        </AppBar>
      </ThemeProvider>
      <Drawer
        variant="permanent"
        classes={{
          paper: clsx(classes.drawerPaper),
        }}
      >
        <Navigation />
      </Drawer>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <div className={classes.pageContainer}>{children}</div>
      </main>
    </div>
  );
};

export { AppLayout };
